import { type SerializedFilters } from '@/infra/FiltersViewModel'
import { cleanSavedJobFilterName } from '@/features/jobs/list/saved-job-filters'

/**
 * A tab someone shared by link.
 */
export interface SharedJobFilter {
  name: string
  filters: SerializedFilters
  /**
   * The default tab the shared one starts from.
   */
  baseId: string | null
  /**
   * The sharer's name, for telling the recipient who it's from.
   */
  sharedBy: string
}

/**
 * The query parameter on the jobs page that carries a shared tab.
 */
export const sharedJobFilterParam = 'view'

/**
 * The longest {@link sharedJobFilterParam} that opens, well above what a
 * tab's filters encode to, so a crafted link can't fill the viewer's storage.
 */
const maxSharedJobFilterParamLength = 8_000

/**
 * Encodes a shared tab for {@link sharedJobFilterParam}.
 */
export function encodeSharedJobFilter(shared: SharedJobFilter): string {
  const bytes = new TextEncoder().encode(JSON.stringify(shared))
  const binary = Array.from(bytes, (b) => String.fromCharCode(b)).join('')
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Decodes {@link sharedJobFilterParam}, or returns `null` when it isn't a
 * shared tab.
 */
export function decodeSharedJobFilter(param: string): SharedJobFilter | null {
  if (param.length > maxSharedJobFilterParamLength) {
    return null
  }
  try {
    const binary = atob(param.replace(/-/g, '+').replace(/_/g, '/'))
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0))
    const parsed = JSON.parse(new TextDecoder().decode(bytes)) as unknown
    return isSharedJobFilter(parsed)
      ? {
          ...parsed,
          name: cleanSavedJobFilterName(parsed.name),
          sharedBy: cleanSavedJobFilterName(parsed.sharedBy),
          baseId: parsed.baseId ?? null,
        }
      : null
  } catch {
    return null
  }
}

function isSharedJobFilter(value: unknown): value is SharedJobFilter {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const shared = value as Partial<SharedJobFilter>
  return (
    typeof shared.name === 'string' &&
    typeof shared.sharedBy === 'string' &&
    typeof shared.filters === 'object' &&
    shared.filters !== null &&
    !Array.isArray(shared.filters) &&
    (shared.baseId === undefined ||
      shared.baseId === null ||
      typeof shared.baseId === 'string')
  )
}
